import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IoNotificationsOutline, IoCheckmarkDoneOutline, IoTrashOutline, IoCloseOutline, IoNotifications } from 'react-icons/io5';
import { formatDistanceToNow } from 'date-fns';
import { enUS, vi } from 'date-fns/locale';
import Avatar from './Avatar';
import { useNotifications } from '../contexts/NotificationContext';
import './NotificationDropdown.css';

const NotificationDropdown = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const listRef = useRef(null);

  // Drag gesture state - use refs to avoid stale closures
  const [isExpanded, setIsExpanded] = useState(false);
  const isDraggingRef = useRef(false);
  const dragStartYRef = useRef(0);
  const dragCurrentYRef = useRef(0);
  // For rendering
  const [, setTick] = useState(0);

  // Check if scrolled to top
  const [isAtTop, setIsAtTop] = useState(true);
  const {
    notifications,
    unreadCount,
    loading,
    hasMore,
    loadMoreNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
  } = useNotifications();

  // Single function to close the notification panel
  const closePanel = useCallback(() => {
    setIsOpen(false);
    setIsExpanded(false);
    isDraggingRef.current = false;
    dragCurrentYRef.current = 0;
    // Blur the button to remove :active state on mobile
    const button = dropdownRef.current?.querySelector('button');
    if (button) button.blur();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        closePanel();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [closePanel]);

  // Disable body scroll on mobile when dropdown is open
  useEffect(() => {
    if (isOpen && window.innerWidth <= 768) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Reset drag state when dropdown closes
  useEffect(() => {
    if (!isOpen) {
      isDraggingRef.current = false;
      dragCurrentYRef.current = 0;
      setIsExpanded(false);
    }
  }, [isOpen]);

  // Document-level touch events for drag gesture - only on header
  useEffect(() => {
    if (!isOpen) return;

    const onTouchStart = (e) => {
      // Only handle if touch is in the header/drag handle area
      const header = dropdownRef.current?.querySelector('.notification-header');
      if (!header) return;

      const rect = header.getBoundingClientRect();
      const touchY = e.touches[0].clientY;

      // Only start drag if touch is on the header area
      if (touchY >= rect.top && touchY <= rect.bottom) {
        isDraggingRef.current = true;
        dragStartYRef.current = touchY;
        dragCurrentYRef.current = touchY;
        setTick((t) => t + 1);
      }
    };

    const onTouchMove = (e) => {
      if (!isDraggingRef.current) return;

      e.preventDefault();
      dragCurrentYRef.current = e.touches[0].clientY;
      setTick((t) => t + 1);
    };

    const onTouchEnd = () => {
      if (!isDraggingRef.current) return;

      const deltaY = dragCurrentYRef.current - dragStartYRef.current;

      // Snap logic:
      // - Drag down > 120px → close
      // - Drag up > 80px → expand
      // - Otherwise snap back to default (70vh)
      if (deltaY > 120) {
        closePanel();
      } else if (deltaY < -80) {
        setIsExpanded(true);
      } else {
        setIsExpanded((prev) => prev);
      }

      isDraggingRef.current = false;
      dragCurrentYRef.current = 0;
    };

    document.addEventListener('touchstart', onTouchStart, { passive: false });
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend', onTouchEnd);

    return () => {
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
    };
  }, [isOpen, isExpanded, closePanel]);

  // Handle scroll to detect if at top
  const handleScroll = (e) => {
    const isScrolledToTop = e.target.scrollTop <= 10;
    setIsAtTop(isScrolledToTop);
  };

  // Calculate drag offset for transform
  const getDragTransform = () => {
    if (!isDraggingRef.current || !isOpen) return 0;

    const deltaY = dragCurrentYRef.current - dragStartYRef.current;

    // When expanded, allow dragging down from any position
    if (isExpanded) {
      return Math.max(0, deltaY);
    }

    // Allow dragging down (to close) or up (to expand) from any position
    return deltaY;
  };

  const dragTransform = getDragTransform();

  const handleNotificationClick = (notification) => {
    // Mark as read if not already
    if (!notification.is_read) {
      markAsRead(notification.id);
    }

    // Navigate based on notification type
    let link = '/';
    switch (notification.type) {
      case 'tag_post':
      case 'reply_post':
      case 'reply_post_tagged':
        if (notification.post_id) {
          link = `/post/${notification.post_id}`;
        }
        break;
      case 'tag_comment':
      case 'reply_comment':
      case 'reply_comment_tagged':
        if (notification.post_id) {
          link = `/post/${notification.post_id}`;
          // Add comment_id as query param to highlight the comment
          if (notification.comment_id) {
            link += `?highlight_comment=${notification.comment_id}`;
          }
        }
        break;
      case 'friend_request':
      case 'friend_accepted':
        link = '/friends';
        break;
      default:
        link = '/';
    }

    closePanel();
    navigate(link);
  };

  const getNotificationText = (notification) => {
    const fromName = notification.from_user?.display_name || 'Someone';
    switch (notification.type) {
      case 'tag_post':
        return t('notifications.taggedYouPost') || 'tagged you in a post';
      case 'tag_comment':
        return t('notifications.taggedYouComment') || 'tagged you in a comment';
      case 'reply_post':
        return t('notifications.repliedToYourPost') || 'replied to your post';
      case 'reply_comment':
        return t('notifications.repliedToYourComment') || 'replied to your comment';
      case 'reply_post_tagged':
        return t('notifications.repliedToPostYoureTagged') || 'replied to a post you\'re tagged in';
      case 'reply_comment_tagged':
        return t('notifications.repliedToCommentYoureTagged') || 'replied to a comment you\'re tagged in';
      case 'friend_request':
        return t('notifications.sentFriendRequest') || 'sent you a friend request';
      case 'friend_accepted':
        return t('notifications.acceptedFriendRequest') || 'accepted your friend request';
      default:
        return notification.content || 'New notification';
    }
  };

  const locale = i18n.language === 'vi' ? vi : enUS;

  return (
    <div className="notification-dropdown" ref={dropdownRef}>
      <button
        className={`nav-link ${isOpen ? 'active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-label={t('notifications.title') || 'Notifications'}
        aria-expanded={isOpen}
      >
        <IoNotificationsOutline />
        {unreadCount > 0 && (
          <span className="notification-badge">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div
          className={`notification-menu ${isExpanded ? 'expanded' : ''}`}
          style={{
            transform: dragTransform > 0 ? `translateY(${dragTransform}px)` : undefined,
            transition: isDraggingRef.current ? 'none' : undefined,
          }}
        >
          <div className="notification-drag-handle" />
          <div className="notification-header">
            <h3>{t('notifications.title') || 'Notifications'}</h3>
            <div className="notification-header-actions">
              {unreadCount > 0 && (
                <button
                  className="mark-all-read"
                  onClick={markAllAsRead}
                  title={t('notifications.markAllRead') || 'Mark all as read'}
                >
                  <IoCheckmarkDoneOutline />
                </button>
              )}
              <button
                className="notification-close"
                onClick={closePanel}
                title="Close"
              >
                <IoCloseOutline />
              </button>
            </div>
          </div>

          <div
            className="notification-list"
            ref={listRef}
            onScroll={handleScroll}
          >
            {loading ? (
              <div className="notification-loading">
                {t('common.loading') || 'Loading...'}
              </div>
            ) : notifications.length === 0 ? (
              <div className="notification-empty">
                <IoNotifications className="notification-empty-icon" />
                <span>{t('notifications.noNotifications') || 'No notifications yet'}</span>
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`notification-item ${!notification.is_read ? 'unread' : ''}`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="notification-avatar">
                    {notification.from_user ? (
                      <Avatar user={notification.from_user} size="small" />
                    ) : (
                      <div className="notification-default-avatar">
                        <IoNotificationsOutline />
                      </div>
                    )}
                  </div>
                  <div className="notification-content">
                    <p className="notification-text">
                      {notification.from_user && (
                        <strong>{notification.from_user.display_name}</strong>
                      )}
                      {' '}{getNotificationText(notification)}
                    </p>
                    <span className="notification-time">
                      {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true, locale })}
                    </span>
                  </div>
                  <button
                    className="notification-delete"
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteNotification(notification.id);
                    }}
                    title={t('common.delete') || 'Delete'}
                  >
                    <IoTrashOutline />
                  </button>
                </div>
              ))
            )}
          </div>
          
          <div className="notification-footer">
            <button 
              className="notification-view-all"
              onClick={() => {
                closePanel();
                navigate('/notifications');
              }}
            >
              {t('notifications.viewAll') || 'View all notifications'}
            </button>
            {hasMore && notifications.length > 0 && (
              <button 
                className="notification-load-more"
                onClick={loadMoreNotifications}
                disabled={loading}
              >
                {loading 
                  ? (t('common.loading') || 'Loading...') 
                  : (t('notifications.loadMore') || 'Load more')
                }
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationDropdown;
